<template>
  <v-container fluid class="max-width-container pa-0 py-6">
    <v-row justify="center" class="gap-y-6">

      <!-- LEFT COLUMN: Configuration summary card -->
      <v-col cols="12" md="5">
        <v-card class="pa-8 border-card text-center shadow-summary h-100 d-flex flex-column" rounded="xl" style="background: white;">
          <div class="mb-6 text-left">
            <div class="text-overline font-weight-black text-slate-500 tracking-wider mb-1">
              CONFIGURATION SUMMARY
            </div>
            <h3 class="text-h5 font-weight-black text-slate-800 mb-0">
              {{ form.acquisition_type === 'lease' ? 'Lease Overview' : 'Purchase Overview' }}
            </h3>
          </div>

          <!-- Specs Table -->
          <div class="specs-table mb-6 text-left flex-grow-1">
            <template v-if="cart_items.length <= 1">
              <div class="d-flex justify-space-between py-3 border-bottom-thin">
                <span class="font-weight-bold text-slate-500">Machine</span>
                <span class="font-weight-black text-slate-800">{{ copier.instance?.name }}</span>
              </div>
            </template>
            <template v-else>
              <div class="py-2 border-bottom-thin">
                <span class="font-weight-bold text-slate-500 d-block mb-2">Configured Machines ({{ cart_items.length }})</span>
                <div v-for="(item, idx) in cart_items" :key="idx" class="d-flex justify-space-between align-center py-1.5 pl-3 border-left text-body-2 mb-1" style="border-left: 2px solid #e06927 !important;">
                  <span class="text-slate-700 font-weight-medium truncate mr-4" style="max-width: 65%;">{{ item.product.name }}</span>
                  <div class="d-flex align-center gap-2 flex-shrink-0">
                    <span class="font-weight-black text-slate-800" v-if="ConfigShowPricing && form.acquisition_type === 'lease' && !isItemUnpriced(item)">
                      ${{ CalculateProductMonthlyPrice(item.product, item.datasets, item.accessories).toLocaleString() }}/mo
                    </span>
                    <span class="text-caption font-weight-bold text-slate-400" v-else>
                      {{ item.product.printer_category || 'Copier' }}
                    </span>
                    <v-btn
                      v-if="cart_items.length > 1"
                      icon="mdi-trash-can-outline"
                      variant="text"
                      density="comfortable"
                      color="red-darken-2"
                      size="small"
                      @click="$emit('remove-item', idx)"
                    />
                  </div>
                </div>
              </div>
            </template>

            <div class="d-flex justify-space-between py-3 border-bottom-thin">
              <span class="font-weight-bold text-slate-500">Term</span>
              <span class="font-weight-black text-slate-800">
                {{ form.acquisition_type === 'lease' ? form.lease_term + ' month lease' : 'Full purchase' }}
              </span>
            </div>
            <template v-if="form.acquisition_type === 'lease' && cart_items.length <= 1">
              <div class="d-flex justify-space-between py-3 border-bottom-thin">
                <span class="font-weight-bold text-slate-500">Black and white</span>
                <span class="font-weight-black text-slate-800">{{ copier.instance?.prints_per_month?.toLocaleString() }} pages</span>
              </div>
              <div class="d-flex justify-space-between py-3 border-bottom-thin" v-if="copier.instance?.copier_color !== 'B&W'">
                <span class="font-weight-bold text-slate-500">Color</span>
                <span class="font-weight-black text-slate-800">{{ copier.instance?.color_volume?.toLocaleString() }} pages</span>
              </div>
            </template>
            <template v-if="form.acquisition_type === 'lease'">
              <div class="d-flex justify-space-between py-3 border-bottom-thin">
                <span class="font-weight-bold text-slate-500">Service and toner</span>
                <span class="font-weight-bold text-green-darken-3">Included</span>
              </div>
            </template>
            <div v-if="free_shipping" class="d-flex justify-space-between py-3 border-bottom-thin">
              <span class="font-weight-bold text-slate-500">Delivery</span>
              <span class="font-weight-bold text-green-darken-3">Free</span>
            </div>
            <div v-if="ConfigShowPricing && is_lease_priced" class="d-flex justify-space-between align-center py-3 border-bottom-thin">
              <span class="font-weight-bold text-slate-500">Estimated Monthly</span>
              <span class="font-weight-black text-slate-800 d-inline-flex align-center">
                $<Odometer :value="cartTotalMonthlyPrice || 0" />/mo
              </span>
            </div>
            <div v-else-if="ConfigShowPricing" class="d-flex justify-space-between align-center py-3 border-bottom-thin">
              <span class="font-weight-bold text-slate-500">Estimated Monthly</span>
              <span class="font-weight-bold text-slate-600">Contact for pricing</span>
            </div>
          </div>

          <!-- Green Notice Banner -->
          <div class="notice-banner py-3 px-4 text-body-2 font-weight-bold text-green-darken-4 rounded-lg mb-4 text-center">
            This is your equipment configuration summary. No obligation to continue.
          </div>

          <!-- Trust Badges -->
          <div class="text-caption font-weight-bold text-slate-500">
            ★ 5.0 Google, 1,240 reviews &bull; BBB A+ Accredited
          </div>
        </v-card>
      </v-col>

      <!-- RIGHT COLUMN: Installation Address Form -->
      <v-col cols="12" md="7">
        <v-card class="pa-8 border-card shadow-summary h-100" rounded="xl" style="background: white;">
          <div class="text-left mb-6">
            <h2 class="text-h5 font-weight-black text-slate-800 mt-0 mb-2">Where will the machine be installed</h2>
            <p class="text-body-2 text-slate-500">We use this to confirm local service coverage and delivery.</p>
          </div>

          <v-form ref="address_form" v-model="addressFormValid" @submit.prevent="SubmitAddress">
            <v-row class="gap-y-4">
              <v-col cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">STREET ADDRESS</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.required]"
                  v-model="form.address.street"
                  placeholder="e.g. 4210 W Kennedy Blvd">
                </v-text-field>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">SUITE OR FLOOR, OPTIONAL</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  v-model="form.address.suite"
                  placeholder="e.g. Suite 200">
                </v-text-field>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">CITY</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  placeholder="e.g. Tampa"
                  v-model="form.address.city"
                  :rules="[rules.required]">
                </v-text-field>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">STATE</span>
                <v-combobox
                  hide-details
                  rounded="lg"
                  :items="states"
                  color="primary"
                  variant="outlined"
                  @blur="OnStateBlur"
                  placeholder="e.g. FL"
                  density="comfortable"
                  :rules="[rules.required]"
                  v-model="form.address.state"
                  @update:model-value="FormatStateAbbreviation">
                </v-combobox>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">ZIP CODE</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  placeholder="e.g. 33609"
                  v-model="form.address.zip"
                  :rules="[rules.required]">
                </v-text-field>
              </v-col>
            </v-row>

            <v-btn
              type="submit"
              :color="ConfigElementsColor"
              block
              rounded="lg"
              size="large"
              elevation="0"
              class="text-none font-weight-bold cta-btn mt-8 mb-3"
            >
              Continue to contact details
            </v-btn>

            <v-btn
              variant="outlined"
              color="primary"
              block
              rounded="lg"
              size="large"
              class="text-none font-weight-bold"
              @click="configurator_step = 'configurator'"
            >
              Adjust my configuration
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import Odometer from './Odometer.vue'
import { CalculateProductMonthlyPrice, IsProductCustomQuote } from '@/mixins/PricingMixins.ts'

const props = defineProps<{
  copier: {
    instance: any
    datasets: any[]
    accessories: any[]
  }
  cartTotalMonthlyPrice?: number
}>()

const emit = defineEmits<{
  (e: 'remove-item', idx: number): void
  (e: 'proceed'): void
}>()

const store = useGlobalsStore()
const { form, states, cart_items, configurator_step, free_shipping, ConfigElementsColor, ConfigShowPricing } = storeToRefs(store)

const isCartUnpriced = computed(() => {
  return cart_items.value.every((item: any) => IsProductCustomQuote(item.product))
})

const isItemUnpriced = (item: any) => {
  return IsProductCustomQuote(item?.product)
}

const is_lease_priced = computed(() => {
  return form.value.acquisition_type === 'lease' && !isCartUnpriced.value && Number(props.cartTotalMonthlyPrice || 0) > 0
})

const address_form = ref<any>(null)
const addressFormValid = ref(false)

const rules = {
  required: (v: any) => !!v || 'Field is required'
}

const us_state_name_to_abbr: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'virgin islands': 'VI',
  'puerto rico': 'PR',
  guam: 'GU'
}

function GetStateAbbreviation(val: any): string {
  if (!val) return ''
  const raw_val = typeof val === 'object' && val !== null ? (val.value ?? val.title ?? '') : val
  const trimmed = String(raw_val).trim()
  if (!trimmed) return ''
  const lower = trimmed.toLowerCase()

  if (us_state_name_to_abbr[lower]) {
    return us_state_name_to_abbr[lower]
  }
  if (trimmed.length <= 2) {
    return trimmed.toUpperCase()
  }
  const found = Object.entries(us_state_name_to_abbr).find(([name]) => lower.startsWith(name) || name.startsWith(lower))
  if (found) {
    return found[1]
  }
  return trimmed.slice(0, 2).toUpperCase()
}

function FormatStateAbbreviation(val?: any) {
  const target_val = val !== undefined ? val : form.value.address.state
  const formatted = GetStateAbbreviation(target_val)
  form.value.address.state = formatted
}

function OnStateBlur(event?: any) {
  const input_val = event?.target?.value ?? form.value.address.state
  if (input_val) {
    FormatStateAbbreviation(input_val)
  }
}

watch(
  () => form.value.address.state,
  (new_val) => {
    if (typeof new_val === 'string' && new_val) {
      const formatted = GetStateAbbreviation(new_val)
      if (formatted && formatted !== new_val) {
        form.value.address.state = formatted
      }
    }
  }
)

function SubmitAddress() {
  if (form.value.address.state) {
    FormatStateAbbreviation(form.value.address.state)
  }
  if (address_form.value?.validate) {
    address_form.value.validate().then((res: any) => {
      if (res.valid) {
        emit('proceed')
      }
    })
  } else if (form.value.address.street && form.value.address.zip && form.value.address.city && form.value.address.state) {
    emit('proceed')
  }
}
</script>

<style scoped lang="scss">
.max-width-container {
  max-width: 1400px !important;
  margin: 0 auto !important;
  width: 100% !important;
}
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.shadow-summary {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
}
.border-bottom-thin {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.notice-banner {
  background-color: #f0fdf4;
}
.price-val {
  letter-spacing: -1px;
  font-size: 38px;
}
.cta-btn {
  font-size: 1.1rem !important;
  height: 52px !important;
}
.text-slate-800 {
  color: #1e293b !important;
}
.text-slate-500 {
  color: #64748b !important;
}
.text-slate-700 {
  color: #334155 !important;
}
.text-slate-400 {
  color: #94a3b8 !important;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
